import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  CreditCard, BookOpen, Clock, CheckCircle, AlertCircle,
  Loader2, History, Mail
} from 'lucide-react';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';
import { formatMoney } from '../../lib/currency';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  active:    { label: 'Active',    color: 'text-[#4CAF50] bg-[#4CAF50]/10' },
  expired:   { label: 'Expired',   color: 'text-[#F5A623] bg-[#F5A623]/10' },
  cancelled: { label: 'Cancelled', color: 'text-[#D72B2B] bg-[#D72B2B]/10' },
  pending:   { label: 'Pending',   color: 'text-[#1A2E6E] bg-[#1A2E6E]/10' },
};

const METHOD_LABELS = {
  pix:      'PIX',
  transfer: 'Bank Transfer',
  cash:     'Cash',
  manual:   'Manual',
  courtesy: 'Courtesy',
  stripe:   'Card (Stripe)',
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const fmtAmount = (a, c = 'EUR') => formatMoney(a, c);

// Credit bar — visual indicator of consumed lessons
const CreditBar = ({ used, total }) => {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const remaining = total - used;
  const color =
    remaining <= 1 ? '#D72B2B' :
    remaining <= 3 ? '#F5A623' : '#4CAF50';

  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-[#1A2E6E]/60">{used} of {total} lessons used</span>
        <span className="font-semibold" style={{ color }}>{remaining} remaining</span>
      </div>
      <div className="h-2.5 bg-[#E8EAF0] rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
        />
      </div>
    </div>
  );
};

export const StudentBilling = () => {
  const [billing, setBilling] = useState(null);
  const [renewalPlans, setRenewalPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = usePublicSettings();
  const contactEmail = settings.contact_email || 'franklytalkingenglish@gmail.com';
  const stripeEnabled = !!settings.stripe_enabled;
  const currency = settings.default_currency || 'EUR';

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/student/billing`),
      axios.get(`${API_URL}/api/student/billing/renewal-options`).catch(() => ({ data: [] })),
    ])
      .then(([billingRes, renewalRes]) => {
        setBilling(billingRes.data);
        setRenewalPlans(renewalRes.data);
      })
      .catch(() => toast.error('Failed to load billing info'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  const { active_enrollment, enrollment_history, payments } = billing || {};
  const remaining = active_enrollment
    ? active_enrollment.credits_total - active_enrollment.credits_used
    : 0;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">My Plan</h1>
        <p className="text-[#1A2E6E]/60 mt-1">Your current subscription and lesson credits</p>
      </div>

      {/* Active plan card */}
      {active_enrollment ? (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-[#E8EAF0] overflow-hidden"
        >
          <div className="p-6 border-b border-[#E8EAF0] bg-gradient-to-r from-[#1A2E6E]/3 to-transparent">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="w-5 h-5 text-[#1A2E6E]" />
                  <span className="font-bold text-[#1A2E6E] text-lg">{active_enrollment.plan_name}</span>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#4CAF50]/10 text-[#4CAF50] text-xs font-medium">
                  <CheckCircle size={11} /> Active
                </span>
              </div>
              <div className="text-right">
                <p className="text-xs text-[#1A2E6E]/50">Expires</p>
                <p className="font-semibold text-[#1A2E6E] text-sm">{fmtDate(active_enrollment.expires_at)}</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <CreditBar
              used={active_enrollment.credits_used}
              total={active_enrollment.credits_total}
            />

            {remaining <= 2 && remaining > 0 && (
              <div className="mt-4 flex items-start gap-3 p-4 bg-[#F5A623]/5 border border-[#F5A623]/20 rounded-xl">
                <AlertCircle className="w-5 h-5 text-[#F5A623] shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-[#1A2E6E]">Almost out of lessons</p>
                  <p className="text-xs text-[#1A2E6E]/60 mt-0.5">
                    You have {remaining} lesson{remaining !== 1 ? 's' : ''} remaining. Contact us to renew your plan.
                  </p>
                  <a
                    href={`mailto:${contactEmail}?subject=Plan Renewal`}
                    className="inline-flex items-center gap-1.5 mt-2 text-xs text-[#D72B2B] font-medium hover:underline"
                  >
                    <Mail size={12} /> Contact to renew
                  </a>
                </div>
              </div>
            )}

            {remaining === 0 && (
              <div className="mt-4 flex items-start gap-3 p-4 bg-[#D72B2B]/5 border border-[#D72B2B]/20 rounded-xl">
                <AlertCircle className="w-5 h-5 text-[#D72B2B] shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-[#1A2E6E]">No lessons remaining</p>
                  <p className="text-xs text-[#1A2E6E]/60 mt-0.5">
                    All your credits have been used. Renew your plan to continue learning.
                  </p>
                  <a
                    href={`mailto:${contactEmail}?subject=Plan Renewal`}
                    className="inline-flex items-center gap-1.5 mt-2 text-xs text-[#D72B2B] font-medium hover:underline"
                  >
                    <Mail size={12} /> Contact to renew
                  </a>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8EAF0] p-8 text-center">
          <div className="w-16 h-16 bg-[#F5A623]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-[#F5A623]" />
          </div>
          <h3 className="font-semibold text-[#1A2E6E] mb-2">No active plan</h3>
          <p className="text-sm text-[#1A2E6E]/60 mb-4">
            {stripeEnabled ? 'Choose a plan below to get started.' : 'Contact us to get started.'}
          </p>
          {stripeEnabled ? (
            <Link
              to="/student/checkout"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D72B2B] text-white rounded-xl text-sm font-medium hover:bg-[#B71C1C] transition-colors"
            >
              <CreditCard size={16} /> Get a plan →
            </Link>
          ) : (
            <a
              href={`mailto:${contactEmail}?subject=Plan Inquiry`}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1A2E6E] text-white rounded-xl text-sm font-medium hover:bg-[#16275C] transition-colors"
            >
              <Mail size={16} /> Contact Us
            </a>
          )}
        </div>
      )}

      {/* Renewal Options */}
      {renewalPlans.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E8EAF0]">
            <h2 className="font-semibold text-[#1A2E6E]">
              {active_enrollment ? 'Renew or upgrade your plan' : 'Available plans'}
            </h2>
          </div>
          <div className="divide-y divide-[#E8EAF0]">
            {renewalPlans.map(plan => (
              <div key={plan.id} className="px-6 py-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1A2E6E]">{plan.name}</p>
                  <p className="text-xs text-[#1A2E6E]/50 mt-0.5">{plan.credits} lessons · {plan.validity_days} days</p>
                </div>
                <p className="font-bold text-[#1A2E6E] shrink-0">{formatMoney(plan.price_brl, plan.currency || currency, { maximumFractionDigits: 0 })}</p>
                {stripeEnabled ? (
                  <Link
                    to={`/student/checkout?plan=${plan.id}`}
                    className="px-4 py-2 bg-[#D72B2B] text-white rounded-lg text-sm font-medium hover:bg-[#B71C1C] transition-colors shrink-0"
                  >
                    Select
                  </Link>
                ) : (
                  <a
                    href={`mailto:${contactEmail}?subject=Plan Renewal - ${plan.name}`}
                    className="px-4 py-2 border border-[#1A2E6E]/20 text-[#1A2E6E] rounded-lg text-sm font-medium hover:bg-[#1A2E6E]/5 transition-colors shrink-0"
                  >
                    Inquire
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enrollment history */}
      {enrollment_history && enrollment_history.length > 1 && (
        <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E8EAF0] flex items-center gap-2">
            <History className="w-5 h-5 text-[#1A2E6E]" />
            <h2 className="font-semibold text-[#1A2E6E]">Plan History</h2>
          </div>
          <div className="divide-y divide-[#E8EAF0]">
            {enrollment_history.map(e => {
              const meta = STATUS_META[e.status] || STATUS_META.pending;
              return (
                <div key={e.id} className="px-6 py-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1A2E6E] truncate">{e.plan_name}</p>
                    <p className="text-xs text-[#1A2E6E]/50 mt-0.5">
                      {e.credits_used}/{e.credits_total} lessons used · activated {fmtDate(e.activated_at)}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium shrink-0 ${meta.color}`}>
                    {meta.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Payments */}
      {payments && payments.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E8EAF0] flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#1A2E6E]" />
            <h2 className="font-semibold text-[#1A2E6E]">Payment History</h2>
          </div>
          <div className="divide-y divide-[#E8EAF0]">
            {payments.map(p => (
              <div key={p.id} className="px-6 py-4 flex items-center gap-4">
                <div className="flex-1">
                  <p className="font-semibold text-[#1A2E6E]">{fmtAmount(p.amount, p.currency || currency)}</p>
                  <p className="text-xs text-[#1A2E6E]/50 mt-0.5">
                    {METHOD_LABELS[p.method] || p.method}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#1A2E6E]/50">
                  <Clock size={12} />
                  {fmtDate(p.paid_at || p.created_at)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contact footer */}
      <div className="text-center text-sm text-[#1A2E6E]/50">
        Questions about your plan?{' '}
        <a href={`mailto:${contactEmail}`} className="text-[#D72B2B] hover:underline">
          {contactEmail}
        </a>
      </div>
    </div>
  );
};

export default StudentBilling;
